import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MediaObserver } from '@ngbracket/ngx-layout';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn
} from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import type { MatFormFieldAppearance } from '@angular/material/form-field';
import { FloatLabelType, MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { onlyNumberKeysAndDotAllowed } from '@mng/mng-keyboard-helper';
import { dateToIsoDateString, getFormattedDateString, isoDateStringToDate } from '@mng/mng-ts-helpers';
import { message } from '../form/messages';
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MngGermanDateAdapter } from '../date/mng-german-date-adapter.service';
import { MatInput } from '@angular/material/input';

@Component({
  selector: 'mng-datepicker',
  imports: [
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatNativeDateModule,
    MatInput,
    MatFormField,
    MatSuffix,
    MatLabel,
    MatHint,
    MatError,
    ReactiveFormsModule
  ],
  templateUrl: './mng-datepicker.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `
  ],
  providers: [
    { provide: DateAdapter, useClass: MngGermanDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MngDatepickerComponent),
      multi: true
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MngDatepickerComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MngDatepickerComponent implements ControlValueAccessor, Validator, OnChanges, OnInit {
  media = inject(MediaObserver);

  _formControl = new FormControl();

  @Input()
  required = false;
  @Input()
  floatPlaceholder: FloatLabelType = 'auto';
  @Input()
  autocomplete: string;
  @Input()
  hint: string;
  @Input()
  rawMode: boolean;
  @Input()
  outerErrors: ValidationErrors;
  @Input()
  min: Date;
  @Input()
  max: Date;
  @Input()
  startAt: Date;
  @Input()
  startView: 'month' | 'year' | 'multi-year';
  @Input()
  inputId: string;
  @Input()
  label: string;
  @Input({ transform: booleanAttribute })
  fullWidth = false;
  @Input()
  width = null;
  @ViewChild('dateInput', { read: ElementRef, static: true })
  input: ElementRef;

  @Input({ transform: booleanAttribute })
  touchedOnInit = false;

  @Input()
  appearance: MatFormFieldAppearance = 'outline';

  onChange: any = () => {
    return;
  };
  onTouched: any = () => {
    return;
  };
  onValidityChange: any = () => {
    return;
  };

  ngOnInit(): void {
    if (this.touchedOnInit) {
      this._formControl.markAsTouched();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('outerErrors' in changes) {
      const errors = changes['outerErrors'].currentValue;
      if (errors && Object.entries(errors).length === 0) {
        this._formControl.updateValueAndValidity();
      } else {
        this._formControl.setErrors(errors);
      }
    }
  }

  writeValue(value: string) {
    this._formControl.setValue(value);
  }

  onInputChange(event: MatDatepickerInputEvent<Date>) {
    const dateValue = event.value;
    if (dateValue instanceof Date) {
      const isoDate = dateToIsoDateString(event.value);
      this.onChange(isoDate);
    } else if (dateValue === null) {
      if (event.target['_elementRef'].nativeElement.value) {
        if (this.rawMode) {
          this.onChange(event.target['_elementRef'].nativeElement.value); // set unparsable date in outer formControl (needed for model)
        } else {
          this._formControl.setErrors({ wrongDateFormat: true });
          this.onChange(null);
          this.onValidityChange();
        }
      } else {
        this.onChange(null);
      }
    }
  }

  onInput(event: Event) {
    // handle input of string not firing dateInput Event on mat-datepicker
    if (!this._formControl.value) {
      if (event.currentTarget['value']) {
        this._formControl.setErrors({ wrongDateFormat: true });
        if (this.rawMode) {
          this.onChange(event.currentTarget['value']); // set unparsable date in outer formControl (needed for model)
        } else {
          this.onChange(null);
          this.onValidityChange();
        }
      } else {
        this._formControl.setErrors(null);
        this.onChange(null);
        this.onValidityChange();
      }
    }
  }

  registerOnChange(fn: (value: any) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidityChange = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this._formControl.disable();
    } else {
      this._formControl.enable();
    }
  }

  validate(control: FormControl): ValidationErrors {
    const errors = Object.assign({}, this._formControl.errors || {});
    return Object.keys(errors).length > 0 && this._formControl.invalid ? errors : null;
  }

  onBlur($event) {
    this.onTouched();
  }

  get startAtDate(): Date {
    if (!this._formControl.value) {
      return this.startAt;
    } else if (this._formControl.value instanceof Date) {
      return this._formControl.value;
    } else {
      return isoDateStringToDate(this._formControl.value);
    }
  }
  get startViewOption(): 'month' | 'year' | 'multi-year' {
    return this._formControl.value || !this.startView ? 'month' : this.startView;
  }

  getErrorMessage(): string {
    const errorKey = Object.keys(this._formControl.errors)[0];
    const error = this._formControl.getError(errorKey);

    if (errorKey === 'matDatepickerMin') {
      return `"${this.label}" darf nicht kleiner als ${getFormattedDateString(error.min)} sein.`;
    } else if (errorKey === 'matDatepickerMax') {
      return `"${this.label}" darf nicht größer als ${getFormattedDateString(error.max)} sein.`;
    } else if (errorKey === 'matDatepickerParse') {
      return 'Das Datum ist ungültig';
    } else if (typeof error === 'string') {
      return error;
    } else {
      return message(errorKey);
    }
  }

  callKeydown(event: KeyboardEvent) {
    if (!onlyNumberKeysAndDotAllowed(event)) {
      event.preventDefault();
    }
  }

  focus() {
    this.input.nativeElement.focus();
  }

  @Input()
  set validators(validators: ValidatorFn[]) {
    if (this._formControl && validators) {
      this._formControl.setValidators(validators);
      this._formControl.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    }
  }
}
